import { gql } from '@apollo/client';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { GenericAutocomplete, AutocompleteConfig } from './generic-autocomplete';
import type { ParkingSessionViewModel } from '@/graphql/generated';
import { formatDateTime } from '@/pages/parking-sessions/utils';
import { useTranslation } from 'react-i18next';

export interface ParkingSessionAutocompleteProps {
	value: string;
	onChange: (value: string, session: ParkingSessionViewModel | null) => void;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
	inputClassName?: string;
	leadingIcon?: React.ReactNode;
	initialSelectedLabel?: string;
	openDelay?: number;
}

const PARKING_SESSIONS_AUTOCOMPLETE_QUERY = gql`
	query ParkingSessionsAutocomplete(
		$where: ParkingSessionViewModelFilterInput
		$first: Int
		$after: String
		$order: [ParkingSessionViewModelSortInput!]
	) {
		parkingSessions(where: $where, first: $first, after: $after, order: $order) {
			edges {
				node {
					parkingSessionId
					ticketId
					entryDateTime
					exitDateTime
					isInside
					vehicle {
						plateNumber
					}
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

const createParkingSessionAutocompleteConfig = (
	t: (key: string) => string
): AutocompleteConfig<ParkingSessionViewModel> => ({
	query: PARKING_SESSIONS_AUTOCOMPLETE_QUERY,
	queryName: 'parkingSessions',
	searchFields: ['ticketId', 'vehicle.plateNumber'],
	getDisplayName: session =>
		session.vehicle?.plateNumber ?? session.ticketId ?? t('entityName'),
	getSecondaryText: session => {
		const ticketText = session.ticketId
			? `${t('list.columns.ticket')} ${session.ticketId}`
			: undefined;
		const entryDate = formatDateTime(session.entryDateTime);
		const entryText = entryDate
			? `${t('list.columns.entry')} ${entryDate}`
			: undefined;
		return [ticketText, entryText].filter(Boolean).join(' • ');
	},
	getId: session => session.parkingSessionId,
	pageSize: DEFAULT_PAGE_SIZE,
	orderBy: [{ entryDateTime: 'DESC' }]
});

export const ParkingSessionAutocomplete: React.FC<ParkingSessionAutocompleteProps> = ({
	value,
	onChange,
	placeholder = 'Search parking sessions...',
	disabled = false,
	className = '',
	inputClassName = '',
	leadingIcon,
	initialSelectedLabel,
	openDelay = 0
}) => {
	const { t } = useTranslation('parkingSessions');
	const config = createParkingSessionAutocompleteConfig(t);

	return (
		<GenericAutocomplete<ParkingSessionViewModel>
			config={config}
			value={value}
			onChange={onChange}
			placeholder={placeholder}
			disabled={disabled}
			className={className}
			inputClassName={inputClassName}
			leadingIcon={leadingIcon}
			initialSelectedLabel={initialSelectedLabel}
			openDelay={openDelay}
		/>
	);
};
